import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const toggleLang = () => {
    const next = i18n.language.startsWith('es') ? 'en' : 'es';
    i18n.changeLanguage(next);
  };

  const currentLang = i18n.language.startsWith('es') ? 'ES' : 'EN';
  const otherLang = i18n.language.startsWith('es') ? 'EN' : 'ES';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#F7F5F0] border-b border-[#E8E4DC]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => scrollTo('hero')} className="cursor-pointer flex items-center">
          <img
            src="https://static.readdy.ai/image/342255b0bb01b37d074e93c86c4d5fe5/5dac46d95f2dd311368d9a0b4d745fe3.svg"
            alt="Alice & Bob Capital"
            className="h-10 w-auto"
          />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { labelKey: 'nav_services', id: 'servicios' },
            { labelKey: 'nav_portfolio', id: 'portfolio' },
            { labelKey: 'nav_about', id: 'sobre' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`font-['DM_Sans'] text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-[#0B0F1A] hover:text-[#C4553A]' : 'text-[#F7F5F0]/80 hover:text-white'
              }`}
            >
              {t(item.labelKey)}
            </button>
          ))}

          {/* Language switcher */}
          <button
            onClick={toggleLang}
            className={`font-['JetBrains_Mono'] text-[11px] tracking-[0.15em] font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1 ${
              scrolled ? 'text-[#6B7280] hover:text-[#0B0F1A]' : 'text-[#F7F5F0]/50 hover:text-[#F7F5F0]'
            }`}
            title={`Switch to ${otherLang}`}
          >
            <span className={scrolled ? 'text-[#0B0F1A]' : 'text-[#F7F5F0]'}>{currentLang}</span>
            <span className="opacity-40">/</span>
            <span>{otherLang}</span>
          </button>

          <button
            onClick={() => scrollTo('contacto')}
            className="font-['DM_Sans'] text-sm font-medium bg-[#C4553A] text-white px-5 py-2 rounded-md hover:bg-[#b04030] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('nav_contact')}
          </button>
        </nav>

        {/* Mobile: lang + hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`font-['JetBrains_Mono'] text-[11px] tracking-[0.12em] cursor-pointer ${scrolled ? 'text-[#6B7280]' : 'text-[#F7F5F0]/60'}`}
          >
            {currentLang}
          </button>
          <button
            className={`w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-[#0B0F1A]' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#F7F5F0] border-t border-[#E8E4DC] px-6 py-4 flex flex-col gap-4">
          {[
            { labelKey: 'nav_services', id: 'servicios' },
            { labelKey: 'nav_portfolio', id: 'portfolio' },
            { labelKey: 'nav_about', id: 'sobre' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="font-['DM_Sans'] text-sm font-medium text-[#0B0F1A] hover:text-[#C4553A] text-left cursor-pointer"
            >
              {t(item.labelKey)}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contacto')}
            className="font-['DM_Sans'] text-sm font-medium bg-[#C4553A] text-white px-5 py-2 rounded-md w-fit cursor-pointer whitespace-nowrap"
          >
            {t('nav_contact')}
          </button>
        </div>
      )}
    </header>
  );
}
